import React, { useState, useEffect, useMemo } from 'react';
import { userAPI, attendanceAPI } from '../../../services';
import {
  LoadingSpinner,
  Badge,
  ErrorMessage,
} from '../../UI';
import {
  Clock, ChevronLeft, ChevronRight, MapPin, Calendar, Star,
  XCircle, X, Coffee, AlertTriangle, TrendingUp, UsersRound,
  Activity, Navigation, RotateCcw, CheckCircle, Ban
} from 'lucide-react';

const TeamAttendancePage = () => {
  const [reportees, setReportees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [attendance, setAttendance] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [stats, setStats] = useState({});
  const [employeeDateOfJoining, setEmployeeDateOfJoining] = useState(null);
  const [weekoffConfig, setWeekoffConfig] = useState({
    weekoffType: 'fixed',
    workingDays: [1, 2, 3, 4, 5],
    rotationWeekoffs: []
  });
  const [shiftInfo, setShiftInfo] = useState(null);
  const [loadingReportees, setLoadingReportees] = useState(true);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [error, setError] = useState('');
  // Modal state
  const [dayModal, setDayModal] = useState(null); // { attendanceData, day, isWeekend, holidayData, isWeekendWork, isFutureDate, isMissedPunchOut }

  // Revoke state
  const [pendingRevokes, setPendingRevokes] = useState([]);
  const [revokesLoading, setRevokesLoading] = useState(false);
  const [showRevokesPanel, setShowRevokesPanel] = useState(false);
  const [revokeActionLoading, setRevokeActionLoading] = useState(null); // id of revoke being actioned
  const [revokeComments, setRevokeComments] = useState('');
  const [revokeActionTarget, setRevokeActionTarget] = useState(null); // { id, action: 'approve'|'reject' }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ─── Helpers ──────────────────────────────────────────────────────────────
  const getWeekOfMonth = (date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    return Math.ceil((date.getDate() + firstDay.getDay()) / 7);
  };

  const isWeekoffDay = (date) => {
    const dayOfWeek = date.getDay();
    if (weekoffConfig.weekoffType === 'fixed') {
      return !weekoffConfig.workingDays.includes(dayOfWeek);
    }
    if (weekoffConfig.weekoffType === 'rotation') {
      const weekNumber = getWeekOfMonth(date);
      const weekConfig = weekoffConfig.rotationWeekoffs?.find(r => r.weekNumber === weekNumber);
      if (weekConfig) return weekConfig.weekoffDays.includes(dayOfWeek);
      if (weekoffConfig.rotationWeekoffs?.length > 0) {
        const last = weekoffConfig.rotationWeekoffs[weekoffConfig.rotationWeekoffs.length - 1];
        return last.weekoffDays.includes(dayOfWeek);
      }
    }
    return false;
  };

  const formatDateStr = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const getAttendanceForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return attendance.find(a => formatDateStr(a.date) === dateStr);
  };

  const getHolidayForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return holidays.find(h => formatDateStr(h.date) === dateStr);
  };

  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay();

  const getStatusColor = (status, missedPunchOut = false) => {
    if (missedPunchOut) return 'bg-red-500';
    const map = {
      present: 'bg-green-500', absent: 'bg-red-500', half_day: 'bg-amber-500',
      leave: 'bg-blue-500', weekend: 'bg-slate-200', holiday: 'bg-purple-500', lop: 'bg-orange-500'
    };
    return map[status] || 'bg-gray-300';
  };

  const getStatusBadge = (status, missedPunchOut = false) => {
    if (missedPunchOut) return { label: 'No Punch Out', cls: 'bg-red-100 text-red-700 border border-red-200' };
    const map = {
      present:  { label: 'Present',   cls: 'bg-green-100 text-green-700 border border-green-200' },
      absent:   { label: 'Absent',    cls: 'bg-red-100 text-red-700 border border-red-200' },
      half_day: { label: 'Half Day',  cls: 'bg-amber-100 text-amber-700 border border-amber-200' },
      leave:    { label: 'On Leave',  cls: 'bg-blue-100 text-blue-700 border border-blue-200' },
      holiday:  { label: 'Holiday',   cls: 'bg-purple-100 text-purple-700 border border-purple-200' },
      weekend:  { label: 'Weekend',   cls: 'bg-slate-100 text-slate-500 border border-slate-200' },
      lop:      { label: 'LOP',       cls: 'bg-orange-100 text-orange-700 border border-orange-200' },
    };
    return map[status] || { label: status || 'Unknown', cls: 'bg-gray-100 text-gray-600 border border-gray-200' };
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '--:--';
    return new Date(timeStr).toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '--';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const openMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank', 'noopener,noreferrer');
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchReportees = async () => {
      try {
        const res = await userAPI.getMyReportees();
        const emps = res?.data?.employees || res?.data?.reportees || [];
        setReportees(emps);
        if (emps.length > 0) setSelectedEmployee(emps[0]);
      } catch {
        setError('Failed to load team members');
      } finally {
        setLoadingReportees(false);
      }
    };
    fetchReportees();
  }, []);

  useEffect(() => {
    if (!selectedEmployee) return;
    const fetch = async () => {
      setLoadingAttendance(true);
      setDayModal(null);
      try {
        const res = await attendanceAPI.getReporteeAttendanceHistory(selectedEmployee._id, {
          month: currentMonth + 1, year: currentYear
        });
        const d = res?.data || {};
        setAttendance(d.attendance || []);
        setHolidays(d.holidays || []);
        setStats(d.stats || {});
        setShiftInfo(d.shiftInfo || null);
        setEmployeeDateOfJoining(d.employee?.dateOfJoining ? new Date(d.employee.dateOfJoining) : null);
        if (d.weekoffConfig) {
          setWeekoffConfig(d.weekoffConfig);
        } else {
          setWeekoffConfig({ weekoffType: 'fixed', workingDays: d.workingDays || [1, 2, 3, 4, 5], rotationWeekoffs: [] });
        }
      } catch {
        setError('Failed to load attendance data');
        setAttendance([]);
        setHolidays([]);
      } finally {
        setLoadingAttendance(false);
      }
    };
    fetch();
  }, [selectedEmployee, currentMonth, currentYear]);

  // ─── Revoke helpers ───────────────────────────────────────────────────────
  const fetchPendingRevokes = async () => {
    setRevokesLoading(true);
    try {
      const res = await attendanceAPI.getPendingRevokes();
      setPendingRevokes(res?.data?.revokes || []);
    } catch {
      // silently fail
    } finally {
      setRevokesLoading(false);
    }
  };

  useEffect(() => { fetchPendingRevokes(); }, []);

  const handleRevokeAction = async () => {
    if (!revokeActionTarget) return;
    const { id, action } = revokeActionTarget;
    setRevokeActionLoading(id);
    try {
      if (action === 'approve') {
        await attendanceAPI.approveRevoke(id, { comments: revokeComments });
      } else {
        await attendanceAPI.rejectRevoke(id, { comments: revokeComments });
      }
      setRevokeActionTarget(null);
      setRevokeComments('');
      fetchPendingRevokes();
    } catch (err) {
      setError(err.message || 'Action failed');
    } finally {
      setRevokeActionLoading(null);
    }
  };

  // ─── Month nav ────────────────────────────────────────────────────────────
  const handlePrevMonth = () => {
    setDayModal(null);
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const handleNextMonth = () => {
    setDayModal(null);
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };
  const handleToday = () => {
    setCurrentMonth(new Date().getMonth());
    setCurrentYear(new Date().getFullYear());
    setDayModal(null);
  };

  // ─── Stats ────────────────────────────────────────────────────────────────
  const calendarStats = useMemo(() => ({
    present:     attendance.filter(a => a.status === 'present').length,
    halfDays:    attendance.filter(a => a.status === 'half_day').length,
    leaves:      attendance.filter(a => a.status === 'leave').length,
    lateArrivals:attendance.filter(a => a.isLateArrival).length,
    absent:      stats.absent || 0,
    holidays:    holidays.length
  }), [attendance, holidays, stats]);

  // ─── Build cell info for a day ────────────────────────────────────────────
  const getDayInfo = (day) => {
    const attendanceData = getAttendanceForDate(day);
    const holidayData    = getHolidayForDate(day);
    const today          = new Date(); today.setHours(0, 0, 0, 0);
    const dateObj        = new Date(currentYear, currentMonth, day); dateObj.setHours(0, 0, 0, 0);
    const joiningDateObj = employeeDateOfJoining ? new Date(employeeDateOfJoining) : null;
    if (joiningDateObj) joiningDateObj.setHours(0, 0, 0, 0);

    const isToday       = today.getTime() === dateObj.getTime();
    const isFuture      = dateObj > today;
    const isPast        = dateObj < today;
    const isBeforeJoin  = joiningDateObj && dateObj < joiningDateObj;
    const isWeekend     = isWeekoffDay(dateObj);
    const isMissedPO    = !!(attendanceData?.missedPunchOut);
    const isWeekendWork = isWeekend && attendanceData &&
      (attendanceData.effectiveStatus === 'present' || attendanceData.effectiveStatus === 'half_day' ||
       (attendanceData.status === 'present' && !attendanceData.missedPunchOut) || attendanceData.status === 'half_day');

    return { attendanceData, holidayData, isToday, isFuture, isPast, isBeforeJoin, isWeekend, isMissedPO, isWeekendWork };
  };

  // ─── Calendar grid ────────────────────────────────────────────────────────
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay    = getFirstDayOfMonth(currentMonth, currentYear);
    const cells = [];

    for (let i = 0; i < firstDay; i++) cells.push(<div key={`e-${i}`} className="aspect-square" />);

    for (let day = 1; day <= daysInMonth; day++) {
      const { attendanceData, holidayData, isToday, isFuture, isBeforeJoin, isWeekend, isMissedPO, isWeekendWork } = getDayInfo(day);
      const isSelected = dayModal?.day === day;

      let cellClass = 'bg-white hover:bg-slate-50 border border-slate-200';
      let textClass  = 'text-slate-700';
      let showStatus = null;
      const displayStatus = attendanceData?.effectiveStatus || attendanceData?.status;

      if (holidayData) {
        cellClass = 'bg-purple-500 hover:bg-purple-600'; textClass = 'text-white'; showStatus = 'holiday';
      } else if (isWeekendWork) {
        cellClass = 'bg-emerald-500 hover:bg-emerald-600 ring-2 ring-emerald-300 ring-offset-1'; textClass = 'text-white'; showStatus = 'extra_work';
      } else if (attendanceData) {
        cellClass = `${getStatusColor(displayStatus, isMissedPO)} hover:opacity-90`; textClass = 'text-white';
        showStatus = isMissedPO ? 'missed_punchout' : displayStatus;
      } else if (isWeekend) {
        cellClass = 'bg-slate-100 hover:bg-slate-200 border border-slate-200'; textClass = 'text-slate-400'; showStatus = 'weekend';
      } else if (isBeforeJoin) {
        cellClass = 'bg-gray-100 border border-gray-200'; textClass = 'text-gray-400';
      } else if (!isFuture) {
        cellClass = 'bg-red-100 hover:bg-red-200 border border-red-200'; textClass = 'text-red-600'; showStatus = 'absent';
      }

      cells.push(
        <div
          key={day}
          onClick={() => {
            const info = getDayInfo(day);
            setDayModal({ day, ...info });
          }}
          className={`aspect-square relative cursor-pointer transition-all duration-150 rounded-lg overflow-hidden
            ${isToday ? 'ring-2 ring-indigo-500 ring-offset-2' : ''}
            ${isSelected ? 'ring-2 ring-indigo-400 scale-105 shadow-lg z-10' : 'hover:scale-105'}`}
        >
          <div className={`w-full h-full flex flex-col items-center justify-center ${cellClass} ${textClass}`}>
            <span className="text-sm font-bold">{day}</span>
            {showStatus === 'extra_work'     && <TrendingUp  className="w-3 h-3 mt-0.5" />}
            {showStatus === 'holiday'        && <Star        className="w-3 h-3 mt-0.5" />}
            {showStatus === 'weekend'        && <span className="text-[9px] font-medium mt-0.5">OFF</span>}
            {showStatus === 'present'        && <span className="text-[10px] font-medium mt-0.5">{attendanceData?.workDuration ? `${Math.floor(attendanceData.workDuration / 60)}h` : '✓'}</span>}
            {showStatus === 'half_day'       && <span className="text-[10px] font-medium mt-0.5">½</span>}
            {showStatus === 'leave'          && <Coffee      className="w-3 h-3 mt-0.5" />}
            {showStatus === 'absent'         && !isWeekend && !isMissedPO && <XCircle className="w-3 h-3 mt-0.5" />}
            {showStatus === 'missed_punchout'&& <span className="text-[8px] font-medium mt-0.5">NO OUT</span>}
            {attendanceData?.isLateArrival && (showStatus === 'present' || showStatus === 'extra_work') && (
              <div className="absolute top-0.5 right-0.5 w-2 h-2 bg-orange-400 rounded-full" />
            )}
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((name, idx) => (
            <div key={name} className={`text-center text-xs font-bold py-2 rounded-lg ${isWeekoffDay(new Date(currentYear, currentMonth, 7 + idx - new Date(currentYear, currentMonth, 1).getDay())) ? 'text-red-500 bg-red-50' : 'text-slate-600 bg-slate-50'}`}>
              {name}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{cells}</div>
      </>
    );
  };

  // ─── Day list ─────────────────────────────────────────────────────────────
  const listDays = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const rows = [];
    for (let day = daysInMonth; day >= 1; day--) {
      const { attendanceData, holidayData, isFuture, isBeforeJoin, isWeekend, isMissedPO, isWeekendWork } = getDayInfo(day);
      const dateObj = new Date(currentYear, currentMonth, day); dateObj.setHours(0, 0, 0, 0);
      if (isFuture && dateObj.getTime() !== today.getTime()) continue;
      if (isBeforeJoin) continue;
      rows.push({ day, attendanceData, holidayData, isWeekend, isMissedPO, isWeekendWork });
    }
    return rows;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendance, holidays, currentMonth, currentYear, weekoffConfig, employeeDateOfJoining]);

  // ─── Day Modal ────────────────────────────────────────────────────────────
  const DayModal = ({ info, onClose }) => {
    const { day, attendanceData, holidayData, isWeekend, isFuture, isBeforeJoin, isMissedPO, isWeekendWork } = info;
    const displayStatus = attendanceData?.effectiveStatus || attendanceData?.status;

    const headerBg = holidayData ? 'from-purple-600 to-purple-700' :
      isWeekendWork ? 'from-emerald-600 to-emerald-700' :
      isMissedPO ? 'from-red-600 to-red-700' :
      displayStatus === 'present'  ? 'from-green-600 to-green-700' :
      displayStatus === 'absent'   ? 'from-red-600 to-red-700' :
      displayStatus === 'half_day' ? 'from-amber-500 to-amber-600' :
      displayStatus === 'leave'    ? 'from-blue-600 to-blue-700' :
      isWeekend ? 'from-slate-500 to-slate-600' :
      isFuture  ? 'from-slate-400 to-slate-500' :
      'from-red-600 to-red-700';

    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/40 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`bg-gradient-to-r ${headerBg} text-white p-5 flex items-center justify-between flex-shrink-0`}>
            <div>
              <h3 className="text-xl font-bold">{monthNames[currentMonth]} {day}, {currentYear}</h3>
              <p className="text-sm opacity-80">
                {new Date(currentYear, currentMonth, day).toLocaleDateString('en-US', { weekday: 'long' })}
                {selectedEmployee && ` · ${selectedEmployee.name}`}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {/* Holiday */}
            {holidayData && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg"><Star className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wide">Holiday</p>
                  <p className="font-bold text-purple-900">{holidayData.name}</p>
                </div>
              </div>
            )}

            {/* Weekend Extra Work */}
            {isWeekendWork && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Extra Work Day</p>
                  <p className="font-semibold text-emerald-900">Worked on Weekend</p>
                </div>
              </div>
            )}

            {attendanceData ? (
              <>
                {/* Status */}
                <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</span>
                  <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${getStatusBadge(displayStatus, isMissedPO).cls}`}>
                    {getStatusBadge(displayStatus, isMissedPO).label}
                  </span>
                </div>
                {isMissedPO && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-700 text-sm">
                    <XCircle className="w-4 h-4 flex-shrink-0" />
                    Punched in but did not punch out
                  </div>
                )}

                {/* Late / Early warnings */}
                {(attendanceData.isLateArrival || attendanceData.isEarlyDeparture) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                    {attendanceData.isLateArrival && (
                      <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                        <AlertTriangle className="w-4 h-4" /> Late Arrival
                      </div>
                    )}
                    {attendanceData.isEarlyDeparture && (
                      <div className="flex items-center gap-2 text-amber-800 text-sm font-medium">
                        <AlertTriangle className="w-4 h-4" /> Early Departure
                      </div>
                    )}
                  </div>
                )}

                {/* Punch In */}
                {attendanceData.punchIn?.time && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <Clock className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-green-600 uppercase tracking-wide">Punch In</p>
                        {attendanceData.punchIn.location?.latitude ? (
                          <button
                            onClick={() => openMaps(attendanceData.punchIn.location.latitude, attendanceData.punchIn.location.longitude)}
                            className="text-xl font-bold text-green-900 mt-0.5 hover:text-green-600 hover:underline flex items-center gap-1"
                            title="View on Google Maps"
                          >
                            {formatTime(attendanceData.punchIn.time)}
                            <MapPin className="w-4 h-4" />
                          </button>
                        ) : (
                          <p className="text-xl font-bold text-green-900 mt-0.5">{formatTime(attendanceData.punchIn.time)}</p>
                        )}
                        {attendanceData.punchIn.location?.address && (
                          <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{attendanceData.punchIn.location.address}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Punch Out */}
                {attendanceData.punchOut?.time && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                        <Clock className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Punch Out</p>
                        {attendanceData.punchOut.location?.latitude ? (
                          <button
                            onClick={() => openMaps(attendanceData.punchOut.location.latitude, attendanceData.punchOut.location.longitude)}
                            className="text-xl font-bold text-red-900 mt-0.5 hover:text-red-600 hover:underline flex items-center gap-1"
                            title="View on Google Maps"
                          >
                            {formatTime(attendanceData.punchOut.time)}
                            <MapPin className="w-4 h-4" />
                          </button>
                        ) : (
                          <p className="text-xl font-bold text-red-900 mt-0.5">{formatTime(attendanceData.punchOut.time)}</p>
                        )}
                        {attendanceData.punchOut.location?.address && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{attendanceData.punchOut.location.address}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Work Duration */}
                {attendanceData.workDuration > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">Work Duration</p>
                      {attendanceData.overtime > 0 && (
                        <p className="text-xs text-indigo-500 mt-1">
                          +{Math.floor(attendanceData.overtime / 60)}h {attendanceData.overtime % 60}m overtime
                        </p>
                      )}
                    </div>
                    <span className="text-2xl font-bold text-indigo-700">
                      {Math.floor(attendanceData.workDuration / 60)}h {attendanceData.workDuration % 60}m
                    </span>
                  </div>
                )}

                {/* Location Logs */}
                {attendanceData.locationLogs?.length > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Navigation className="w-3.5 h-3.5" />
                      Location Logs ({attendanceData.locationLogs.length})
                    </p>
                    <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                      {attendanceData.locationLogs.map((log, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs bg-slate-50 rounded-lg p-2">
                          <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${log.isWithinRadius ? 'bg-green-500' : 'bg-red-400'}`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-slate-700">
                                {new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                              </span>
                              {log.isWithinRadius === false && (
                                <span className="text-red-600 font-medium">Outside Zone</span>
                              )}
                            </div>
                            {log.location && (
                              <button
                                onClick={() => openMaps(log.location.latitude, log.location.longitude)}
                                className="text-blue-600 hover:underline flex items-center gap-0.5 mt-0.5"
                              >
                                <MapPin className="w-3 h-3" />
                                {log.location.address || `${log.location.latitude?.toFixed(4)}, ${log.location.longitude?.toFixed(4)}`}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Violations */}
                {attendanceData.violations?.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Violations ({attendanceData.violations.length})
                    </p>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {attendanceData.violations.map((v, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-2 text-xs">
                          <p className="font-semibold text-red-700">
                            {new Date(v.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                          </p>
                          <p className="text-red-600 truncate">{v.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                {holidayData ? (
                  <><Star className="w-12 h-12 text-purple-400 mx-auto mb-3" /><p className="text-purple-600 font-medium">Public Holiday</p></>
                ) : isWeekend ? (
                  <><Coffee className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">Weekend — Day Off</p></>
                ) : isFuture ? (
                  <><Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" /><p className="text-slate-500 font-medium">Future Date</p></>
                ) : (
                  <><XCircle className="w-12 h-12 text-red-300 mx-auto mb-3" /><p className="text-red-600 font-medium">No Attendance Recorded</p><p className="text-xs text-red-400 mt-1">Marked as Absent</p></>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────
  if (loadingReportees) {
    return <div className="flex justify-center items-center h-64"><LoadingSpinner size="lg" /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Clock className="w-7 h-7 text-emerald-600" />
          Team Attendance
        </h2>
        <p className="text-gray-500 mt-1">View team members' attendance calendar &amp; daily logs</p>
      </div>

      {error && <ErrorMessage message={error} onDismiss={() => setError('')} />}

      {/* Pending Revoke Requests */}
      <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
        <button
          onClick={() => setShowRevokesPanel(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-orange-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            <RotateCcw className="w-5 h-5 text-orange-600" />
            <span className="font-semibold text-gray-900">Pending Attendance Revoke Requests</span>
            {pendingRevokes.length > 0 && (
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                {pendingRevokes.length}
              </span>
            )}
          </div>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${showRevokesPanel ? 'rotate-90' : ''}`} />
        </button>

        {showRevokesPanel && (
          <div className="border-t border-orange-100 p-4">
            {revokesLoading ? (
              <div className="flex justify-center py-6"><LoadingSpinner /></div>
            ) : pendingRevokes.length === 0 ? (
              <p className="text-center text-gray-500 py-6 text-sm">No pending revoke requests</p>
            ) : (
              <div className="space-y-3">
                {pendingRevokes.map(revoke => {
                  const dateStr = new Date(revoke.date).toLocaleDateString('en-IN', {
                    day: '2-digit', month: 'short', year: 'numeric'
                  });
                  return (
                    <div key={revoke._id} className="border border-orange-200 rounded-xl p-4 bg-orange-50">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900">{revoke.employee?.name}</p>
                          <p className="text-xs text-gray-500">{revoke.employee?.designation} · {revoke.employee?.department}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Calendar className="w-3.5 h-3.5 text-orange-600" />
                            <span className="text-sm text-orange-700 font-medium">{dateStr}</span>
                            <span className="text-xs text-gray-500">was {revoke.previousStatus?.replace('_', ' ')}</span>
                          </div>
                          <p className="text-sm text-gray-700 mt-2 bg-white rounded-lg px-3 py-2 border border-orange-100">
                            {revoke.reason}
                          </p>
                        </div>
                      </div>

                      {/* Inline action form */}
                      {revokeActionTarget?.id === revoke._id ? (
                        <div className="mt-3 space-y-2">
                          <textarea
                            value={revokeComments}
                            onChange={e => setRevokeComments(e.target.value)}
                            placeholder={`Comments for ${revokeActionTarget.action === 'approve' ? 'approval' : 'rejection'} (optional)`}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={handleRevokeAction}
                              disabled={revokeActionLoading === revoke._id}
                              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${revokeActionTarget.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                            >
                              {revokeActionLoading === revoke._id ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              ) : revokeActionTarget.action === 'approve' ? (
                                <><CheckCircle className="w-4 h-4" />Confirm Approve</>
                              ) : (
                                <><Ban className="w-4 h-4" />Confirm Reject</>
                              )}
                            </button>
                            <button
                              onClick={() => { setRevokeActionTarget(null); setRevokeComments(''); }}
                              className="px-3 py-2 rounded-lg text-sm border border-gray-300 text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => setRevokeActionTarget({ id: revoke._id, action: 'approve' })}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-green-50 border border-green-300 text-green-700 rounded-lg text-sm font-medium hover:bg-green-100 transition-colors"
                          >
                            <CheckCircle className="w-4 h-4" /> Approve
                          </button>
                          <button
                            onClick={() => setRevokeActionTarget({ id: revoke._id, action: 'reject' })}
                            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-red-50 border border-red-300 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors"
                          >
                            <Ban className="w-4 h-4" /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {reportees.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <UsersRound className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No team members found</p>
        </div>
      ) : (
        <>
          {/* Member Selector */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">Team Member</label>
            <div className="flex flex-wrap gap-2">
              {reportees.map((emp) => {
                const active = selectedEmployee?._id === emp._id;
                return (
                  <button
                    key={emp._id}
                    onClick={() => setSelectedEmployee(emp)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                      active ? 'bg-emerald-50 border-emerald-300 text-emerald-700 shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${active ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
                      {emp.name?.charAt(0)?.toUpperCase()}
                    </div>
                    {emp.name}
                  </button>
                );
              })}
            </div>
          </div>

          {selectedEmployee && (
            <>
              {/* Stats bar */}
              <div className="flex gap-3 overflow-x-auto pb-1">
                {[
                  { label: 'Present',  value: calendarStats.present,      color: 'text-green-600',  border: 'border-green-200',  bg: 'bg-green-50'  },
                  { label: 'Absent',   value: calendarStats.absent,       color: 'text-red-600',    border: 'border-red-200',    bg: 'bg-red-50'    },
                  { label: 'Half Day', value: calendarStats.halfDays,     color: 'text-amber-600',  border: 'border-amber-200',  bg: 'bg-amber-50'  },
                  { label: 'Leaves',   value: calendarStats.leaves,       color: 'text-blue-600',   border: 'border-blue-200',   bg: 'bg-blue-50'   },
                  { label: 'Late',     value: calendarStats.lateArrivals, color: 'text-orange-600', border: 'border-orange-200', bg: 'bg-orange-50' },
                  { label: 'Holidays', value: calendarStats.holidays,     color: 'text-purple-600', border: 'border-purple-200', bg: 'bg-purple-50' },
                ].map(s => (
                  <div key={s.label} className={`flex-shrink-0 ${s.bg} rounded-xl border ${s.border} px-5 py-3 text-center min-w-[90px]`}>
                    <p className={`text-xs ${s.color} mb-1`}>{s.label}</p>
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>

              {/* Calendar card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Month nav */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <button onClick={handlePrevMonth} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-center">
                    <h3 className="text-lg font-bold text-gray-900">{monthNames[currentMonth]} {currentYear}</h3>
                    <button onClick={handleToday} className="text-xs text-emerald-600 hover:text-emerald-700 font-medium">Today</button>
                  </div>
                  <button onClick={handleNextMonth} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                {/* Shift badge */}
                {shiftInfo && (
                  <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-slate-50 border-b border-gray-100">
                    <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 rounded-lg border border-indigo-200">
                      <Clock className="w-3 h-3 text-indigo-600" />
                      <span className="text-xs font-semibold text-indigo-700">
                        {shiftInfo.name || 'Shift'}: {shiftInfo.startTime} - {shiftInfo.endTime}
                      </span>
                    </div>
                    {shiftInfo.gracePeriodMinutes > 0 && (
                      <span className="text-xs px-2 py-1 bg-amber-50 rounded-lg border border-amber-200 text-amber-700">Grace: {shiftInfo.gracePeriodMinutes}m</span>
                    )}
                    <span className="text-xs px-2 py-1 bg-blue-50 rounded-lg border border-blue-200 text-blue-700">Min: {shiftInfo.minimumWorkHours}h</span>
                    {shiftInfo.isNightShift && <span className="text-xs px-2 py-1 bg-purple-50 rounded-lg border border-purple-200 text-purple-700">🌙 Night Shift</span>}
                  </div>
                )}

                {/* Grid */}
                <div className="p-4">
                  {loadingAttendance ? (
                    <div className="flex justify-center items-center h-48"><LoadingSpinner size="lg" /></div>
                  ) : renderCalendar()}
                </div>

                {/* Legend */}
                <div className="px-4 pb-4">
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                    {[
                      ['bg-green-500',  'Present'],  ['bg-red-500',   'Absent'],
                      ['bg-amber-500',  'Half Day'], ['bg-blue-500',  'Leave'],
                      ['bg-purple-500', 'Holiday'],  ['bg-slate-200 border border-slate-300', 'Weekend'],
                    ].map(([cls, label]) => (
                      <span key={label} className="flex items-center gap-1">
                        <span className={`w-3 h-3 rounded ${cls}`} /> {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Day List */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-slate-500" />
                  <h4 className="font-semibold text-gray-900">{monthNames[currentMonth]} {currentYear}</h4>
                </div>

                {loadingAttendance ? (
                  <div className="flex justify-center py-10"><LoadingSpinner /></div>
                ) : listDays.length === 0 ? (
                  <div className="text-center py-10 text-gray-400">No records</div>
                ) : (
                  <>
                    {/* Table header */}
                    <div className="hidden sm:grid grid-cols-6 gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <span className="col-span-1">Date</span>
                      <span className="col-span-1">Status</span>
                      <span className="col-span-1">In</span>
                      <span className="col-span-1">Out</span>
                      <span className="col-span-1">Hours</span>
                      <span className="col-span-1" />
                    </div>

                    <div className="divide-y divide-gray-50">
                      {listDays.map(({ day, attendanceData, holidayData, isWeekend, isMissedPO, isWeekendWork }) => {
                        const displayStatus = attendanceData?.effectiveStatus || attendanceData?.status;
                        const badge = holidayData
                          ? { label: holidayData.name, cls: 'bg-purple-100 text-purple-700 border border-purple-200' }
                          : isWeekendWork
                            ? { label: 'Extra Work', cls: 'bg-emerald-100 text-emerald-700 border border-emerald-200' }
                            : isWeekend && !attendanceData
                              ? { label: 'Weekend', cls: 'bg-slate-100 text-slate-500 border border-slate-200' }
                              : getStatusBadge(displayStatus, isMissedPO);
                        const dateObj = new Date(currentYear, currentMonth, day);
                        const isToday = new Date().toDateString() === dateObj.toDateString();

                        return (
                          <div
                            key={day}
                            onClick={() => setDayModal({ day, ...getDayInfo(day) })}
                            className={`flex sm:grid sm:grid-cols-6 gap-2 items-center px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${isToday ? 'bg-blue-50/50' : ''}`}
                          >
                            {/* Date */}
                            <div className="sm:col-span-1 flex-shrink-0 w-14 sm:w-auto">
                              <p className="text-sm font-bold text-gray-900">{String(day).padStart(2, '0')}</p>
                              <p className="text-xs text-gray-400">{shortMonths[currentMonth]} · {dateObj.toLocaleDateString('en-US', { weekday: 'short' })}</p>
                            </div>
                            {/* Status */}
                            <div className="sm:col-span-1 flex-1 sm:flex-none">
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${badge.cls}`}>{badge.label}</span>
                            </div>
                            {/* In */}
                            <div className="sm:col-span-1 hidden sm:block">
                              {attendanceData?.punchIn?.time ? (
                                <span className="text-sm font-semibold text-green-700">{formatTime(attendanceData.punchIn.time)}</span>
                              ) : <span className="text-gray-300">--:--</span>}
                            </div>
                            {/* Out */}
                            <div className="sm:col-span-1 hidden sm:block">
                              {attendanceData?.punchOut?.time ? (
                                <span className="text-sm font-semibold text-red-700">{formatTime(attendanceData.punchOut.time)}</span>
                              ) : <span className="text-gray-300">--:--</span>}
                            </div>
                            {/* Duration */}
                            <div className="sm:col-span-1 hidden sm:block">
                              <span className="text-sm font-semibold text-indigo-700">{formatDuration(attendanceData?.workDuration)}</span>
                            </div>
                            {/* Chevron / late dot */}
                            <div className="sm:col-span-1 flex items-center justify-end gap-2 flex-shrink-0">
                              {attendanceData?.isLateArrival && (
                                <span className="text-[10px] font-semibold bg-orange-100 text-orange-600 border border-orange-200 px-1.5 py-0.5 rounded-full">Late</span>
                              )}
                              <ChevronRight className="w-4 h-4 text-gray-300" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* Day Detail Modal */}
      {dayModal && <DayModal info={dayModal} onClose={() => setDayModal(null)} />}
    </div>
  );
};

export default TeamAttendancePage;
